import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Container from '../../Container';
import { MenuIcon, CartIcon, SearchIcon } from '../../Icon';
import Link from '../../../Router/Link';

import {
  StyledMain,
  Inner,
  UpperMobile,
  Action,
  ActionBadge,
  ActionIcon,
  ActionLink,
  ActionSection,
  ActionText,
  ActionWrapper,
  Logo,
  LogoSection,
} from './wrappers';

interface IMain {
  cartCount?: number;
  onMenuClick?: () => void;
  renderSearch: (isHidden: boolean) => JSX.Element;
}

const Main = ({ cartCount, onMenuClick, renderSearch }: IMain) => {
  const wrapper = useRef<HTMLDivElement | null>(null);
  const [affixPassed, setAffixPassed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const onWindowScroll = () => {
    const rects = wrapper.current?.getBoundingClientRect();
    if (rects && (wrapper.current?.offsetTop || 0) - 42 > rects.height) {
      setAffixPassed(true);
    } else if (affixPassed) {
      setAffixPassed(false);
      setShowSearch(false);
    }
  };

  const onToggleSearch = () => {
    setShowSearch((val) => !val);
  };

  useEffect(() => {
    window.addEventListener('scroll', onWindowScroll);

    return () => {
      window.removeEventListener('scroll', onWindowScroll);
    };
  });

  return (
    <StyledMain ref={wrapper} data-main="">
      <Container>
        <Inner>
          <UpperMobile aria-hidden="true" />
          <ActionSection>
            <ActionWrapper>
              <Action onClick={onMenuClick} title="Öppna menyn">
                <ActionIcon>
                  <MenuIcon className="menu-icon" />
                </ActionIcon>
                <ActionText className="hidden-md">Meny</ActionText>
              </Action>
            </ActionWrapper>
          </ActionSection>
          <LogoSection>
            <Link href="/">
              <Logo xmlns="http://www.w3.org/2000/svg" viewBox="0 0 107 48">
                <title>Mio logotyp</title>
                <g>
                  <path
                    d="M76.3493 17.9371V29.873c0 .4225.3473.7696.77.7696h11.9426c.4227 0 .77-.3471.77-.7696V17.9371c0-.4225-.3473-.7695-.77-.7695H77.1193c-.4227 0-.77.347-.77.7695zM-.00158691 36.8595V11.4788C-.00158691 5.91073 3.8937-.00439453 12.2882-.00439453c7.7 0 10.6442 6.27727453 11.4897 6.27727453.8455 0 3.8198-6.27727453 11.4896-6.27727453 6.3563 0 9.3306 4.11945453 9.3306 4.11945453S45.9267-.00439453 50.124-.00439453h6.7337c4.1369 0 5.4051 4.04400453 5.4051 4.04400453s2.8385-4.04400453 8.6059-4.04400453h24.2777c6.1296 0 11.8216 5.38698453 11.8216 11.51339453v24.928c0 5.9-5.994 11.4077-11.882 11.4077.0302.0302-24.2475 0-24.2475 0-6.1298 0-8.5455-4.2251-8.5455-4.2251s-1.6457 4.2251-5.4655 4.2251h-6.7186c-4.5898 0-5.3749-4.2704-5.3749-4.2704s-2.9894 4.4213-9.1193 4.4213c-2.2949 0-4.2878-1.9918-4.2878-1.9918s-1.4343 1.8258-4.4992 1.8258h-6.1751c-2.2949 0-3.8802-1.5693-3.8802-1.5693s-1.3589 1.7202-4.4238 1.7202C4.709 47.9956-.00158691 41.4467-.00158691 36.8595z"
                    fill="#000"
                  />
                  <path
                    d="M3.21448 36.8596c0 4.4967 5.29943 7.922 8.27372 7.922 3.1102 0 3.2612-2.6407 3.2612-2.6709V17.1375c0-.8903.7247-1.6146 1.6155-1.6146.8908 0 1.6155.7243 1.6155 1.6146V42.488c0 1.343 1.3739 2.1276 2.7176 2.1276h5.8128c1.4645 0 3.1253-.6338 3.1253-2.0974V17.1375c0-.8903.7247-1.6146 1.6155-1.6146.8908 0 1.6155.7243 1.6155 1.6146v24.9732c0 .0151.151 2.6709 3.2612 2.6709 2.9743 0 8.2737-3.4555 8.2737-7.922V11.4789c0-4.17985-4.2124-8.25405-9.0739-8.25405-6.8847 0-8.4248 7.04685-11.4897 7.04685-3.0649 0-4.5898-7.04685-11.4896-7.04685-5.19373 0-9.07393 4.61742-9.07393 8.25405-.06039 9.8987-.06039 16.4627-.06039 25.3807zM50.3959 18.2993c-1.4796 0-2.6875 1.2072-2.6875 2.686v20.9745c0 1.4788 1.1928 2.686 2.6875 2.686h6.2506c1.4796 0 2.6874-1.1921 2.6874-2.686V20.9853c0-1.4788-1.2078-2.686-2.6874-2.686h-6.2506zM50.3959 3.31519c-1.4796 0-2.6875 1.19207-2.6875 2.68594v6.42817c0 1.4788 1.1928 2.686 2.6875 2.686h6.2506c1.4796 0 2.6874-1.2072 2.6874-2.686V6.00113c0-1.47878-1.2078-2.68594-2.6874-2.68594h-6.2506zM70.929 3.2247c-3.7896.01509-8.47 4.1949-8.4398 8.254.0604 8.5105 0 17.1418 0 25.3807 0 3.3801 3.9708 7.7108 8.4398 7.7108h24.1871c3.9104 0 8.6659-4.1949 8.6659-7.7108V11.4787c0-4.31562-4.4837-8.23891-8.6659-8.254-8.0624-.01509-16.1247-.01509-24.1871 0zm5.9637 14.154h.0755l12.0483-.181c.4227 0 .77.347.77.7695l-.3473 11.8604c0 .4226-.3472.7696-.77.7696l-11.4292.2565c-.4228 0-.77-.347-.77-.7695l-.2567-11.951c-.0151-.3923.302-.7243.6794-.7545z"
                    fill="#fff"
                  />
                  <path
                    d="M77.2551 18.8425v10.1252c0 .4225.3473.7695.77.7695h10.1308c.4227 0 .77-.347.77-.7695V18.8425c0-.4225-.3473-.7695-.77-.7695H78.0251c-.4227 0-.77.347-.77.7695zm1.1777-3.9836h9.3004c2.5364 0 4.4086 1.8711 4.4086 4.4061v9.2952c0 2.5351-1.8722 4.4062-4.4086 4.4062h-9.3004c-2.5365 0-4.4087-1.8711-4.4087-4.4062V19.265c.0151-2.5501 1.8722-4.4061 4.4087-4.4061z"
                    fill="#000"
                  />
                </g>
              </Logo>
            </Link>
          </LogoSection>
          {renderSearch(affixPassed && !showSearch)}
          <ActionSection className="is-right">
            <ActionWrapper>
              <Action
                onClick={onToggleSearch}
                className={`${!affixPassed && 'is-hidden'} hidden-md`}
                title="Sök">
                <ActionIcon>
                  <SearchIcon className="search-icon" />
                </ActionIcon>
              </Action>
              <Link
                Component={ActionLink}
                href="/varukorg"
                title="Varukorg"
                className={clsx(!!cartCount && 'is-positive')}>
                <ActionIcon>
                  <CartIcon className="cart-icon" />
                </ActionIcon>
                {!!cartCount && (
                  <ActionBadge
                    className={clsx(cartCount > 99 && 'extra-padding')}
                    title={`${cartCount} varor i varukorgen`}>
                    {cartCount}
                  </ActionBadge>
                )}
              </Link>
            </ActionWrapper>
          </ActionSection>
        </Inner>
      </Container>
    </StyledMain>
  );
};

export default Main;
